// Debounced autocomplete over the catalog's 7,500+ ingredients.
//
// Wraps POST /api/catalog/lookup-ingredient. The endpoint runs the same
// L2 cascade the grader uses (FTS prefix → fuzzy → exact), so what the
// admin sees here is what the grader would match later. That keeps
// curation and grading aligned.
//
// UX: user types into an input; on focus/change with non-empty value
// we fire a debounced lookup; results render as a dropdown of clickable
// rows. Selecting a row calls `onPick(match)` and clears the input.

import { useEffect, useRef, useState } from 'react';
import { lookupIngredient, type IngredientLookupMatch } from '@/api/admin';

interface IngredientPickerProps {
  /** Called when the user selects an ingredient from the dropdown. */
  onPick: (match: IngredientLookupMatch) => void;
  /** Placeholder text. */
  placeholder?: string;
  /** Optional language hint forwarded to the lookup ('ro' | 'en'). */
  language?: 'ro' | 'en';
}

const DEBOUNCE_MS = 250;

export function IngredientPicker({
  onPick,
  placeholder = 'Search ingredients…',
  language: _language,
}: IngredientPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IngredientLookupMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced fetch — cancels the previous request on each keystroke so
  // we don't see stale results flash. AbortController + a key bump on
  // every effect run gives us cleanup without manual tracking.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      lookupIngredient(trimmed, 10, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted) {
            setResults(res.matches);
            setOpen(true);
          }
        })
        .catch(() => {
          // Silently drop errors — autocomplete is not load-bearing,
          // user can still type-in-and-pick if they know the id.
          if (!controller.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Click outside closes the dropdown.
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  function pick(match: IngredientLookupMatch) {
    onPick(match);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="input w-full"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
      />
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-72 overflow-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">No matches.</div>
          )}
          {results.map((match) => (
            <button
              key={match.ingredient_id}
              type="button"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
              onClick={() => pick(match)}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-slate-900">
                  {match.ingredient_name_en}
                </span>
                <span className="text-xs text-slate-400">
                  id {match.ingredient_id}
                </span>
              </div>
              {match.ingredient_name_en !== match.ingredient_name_ro && (
                <div className="text-xs text-slate-500">
                  {match.ingredient_name_ro}
                </div>
              )}
              <div className="text-xs text-slate-400">
                {match.tier} · {Math.round(match.confidence * 100)}% · matched “
                {match.matched_alias}”
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
